<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { z } from 'zod';
import { UiButton, UiInputField, UiTextarea, UiText, useApi, useSharedLink } from '@shopexperts/shared';
import { useAuthCheck } from '@/app/composables/leads/useAuthCheck';
import { resolveEmbedParentOrigin } from '@/app/composables/claim/websiteOrigin';
import { useAuthStore } from '@/store/auth';
import { authHandoffQuery, MAX_MESSAGE_LENGTH, messageStartPath, messageTextToHtml, saveMessageDraft } from '@/v2Projects/directMessageDraft';

const route = useRoute();
const embedded = window.parent !== window;
const api = useApi();
const authStore = useAuthStore();
const auth = useAuthCheck();
const expert = typeof route.query.expert === 'string' ? route.query.expert : '';
const name = typeof route.query.name === 'string' ? route.query.name : 'this expert';
const { connected, emitEvent } = useSharedLink({
  name: 'shopexperts-direct-message', default: {}, debug: false,
  channelId: typeof route.query.channelId === 'string' ? route.query.channelId : 'direct-message',
  mode: 'bidirectional', targetOrigin: resolveEmbedParentOrigin(route.query.parentOrigin),
});
const message = ref('');
const email = ref('');
const busy = ref(false);
const error = ref('');
const submitted = ref(false);
const content = ref<HTMLElement | null>(null);
let resizeObserver: ResizeObserver | undefined;
function reportHeight() {
  if (!connected.value || auth.isLoading.value || !content.value) return;
  emitEvent('resize', { height: Math.ceil(content.value.getBoundingClientRect().height) });
}
onMounted(() => {
  resizeObserver = new ResizeObserver(reportHeight);
  if (content.value) resizeObserver.observe(content.value);
  reportHeight();
});
watch([connected, auth.isLoading], reportHeight, { flush: 'post' });
onBeforeUnmount(() => resizeObserver?.disconnect());
const wrongAccount = computed(() => auth.isAuthenticated.value && !auth.isClient.value);
const emailError = computed(() => submitted.value && !z.email().safeParse(email.value.trim()).success ? 'Enter a valid email address.' : '');
const messageError = computed(() => {
  if (!submitted.value) return '';
  if (!message.value.trim()) return 'Write a message to continue.';
  return message.value.length > MAX_MESSAGE_LENGTH ? `Keep your message under ${MAX_MESSAGE_LENGTH.toLocaleString()} characters.` : '';
});
watch(auth.user, user => { if (user?.email) email.value = user.email; }, { immediate: true });

function switchAccount() {
  authStore.logout();
  email.value = '';
}

async function continueToChat() {
  if (busy.value) return;
  submitted.value = true;
  error.value = '';
  if (messageError.value || emailError.value || wrongAccount.value || !expert) return;
  busy.value = true;
  try {
    const signedIn = auth.isClient.value;
    const result = signedIn ? null : await api.public.auth.checkEmail({ body: { email: email.value.trim() } });
    const id = crypto.randomUUID();
    saveMessageDraft(id, { expert, email: email.value.trim(), html: messageTextToHtml(message.value), createdAt: Date.now() });
    const redirect = messageStartPath(expert, id, typeof route.query.cta_source === 'string' ? route.query.cta_source : undefined);
    // Expired local credentials must not make the login guard skip authentication.
    if (!signedIn && authStore.token) authStore.clearCurrentUser();
    const path = signedIn ? redirect
      : `${result?.exists ? '/login' : '/client/signup'}?${new URLSearchParams(authHandoffQuery(email.value, redirect))}`;
    if (window.parent === window) window.location.assign(path);
    else emitEvent('continue', { path });
  } catch {
    error.value = "We couldn't continue. Your message is still here. Please try again.";
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <main ref="content" class="flex flex-col gap-6 bg-white p-6 sm:p-8" @keydown.esc="embedded && !busy && emitEvent('close', {})">
    <div class="flex flex-col gap-2 pr-6">
      <UiText as="h1" variant="h4" :text="`Message ${name}`" />
      <UiText as="p" variant="p-small" class="text-se-gray-600"
        text="Tell them what you need help with. You can review your message in the chat before sending." />
    </div>
    <div v-if="auth.isLoading.value" role="status"><UiText as="p" text="Checking your account…" /></div>
    <div v-else-if="wrongAccount" class="flex flex-col gap-4">
      <UiText as="p" variant="p" text="You're signed in with an expert or admin account. Use a client account to contact this expert." />
      <UiButton label="Use a client account" variant="outline" @click="switchAccount" />
    </div>
    <form v-else class="flex flex-col gap-5" novalidate @submit.prevent="continueToChat">
      <div class="flex flex-col gap-2">
        <label for="message"><UiText as="span" variant="label" text="Your message" /></label>
        <UiTextarea id="message" v-model="message" :rows="5" :disabled="busy" :invalid="!!messageError" :error="messageError"
          placeholder="Hi, I'd like help with my Shopify store…" />
        <UiText as="p" variant="small" class="text-se-gray-500" :text="`${message.length.toLocaleString()} / ${MAX_MESSAGE_LENGTH.toLocaleString()} characters`" />
      </div>
      <div v-if="!auth.isClient.value" class="flex flex-col gap-2">
        <label for="email"><UiText as="span" variant="label" text="Your email" /></label>
        <UiInputField id="email" v-model="email" type="email" autocomplete="email" :disabled="busy"
          :invalid="!!emailError" :error="emailError" placeholder="you@example.com" />
        <UiText as="p" variant="small" class="text-se-gray-600" text="Next, log in or create a free client account. We'll keep your message ready." />
      </div>
      <UiText v-else as="p" variant="p-small" :text="`Continuing as ${email}`" />
      <div v-if="error" role="alert"><UiText as="p" variant="p-small" class="text-red-700" :text="error" /></div>
      <UiButton type="submit" :label="busy ? 'Continuing…' : auth.isClient.value ? 'Continue to chat' : 'Continue'"
        :disabled="busy || (!connected && embedded)" class="w-full" />
    </form>
  </main>
</template>
